import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import {
  privacyNoticeDocumentUrl,
  termsOfServiceDocumentUrl,
} from '../../shared/consent/legalDocumentUrls.js';
import type { RegistrationConsentsFormState } from './authConsents.js';

interface RegistrationConsentsFieldProps {
  value: RegistrationConsentsFormState;
  onChange: (next: RegistrationConsentsFormState) => void;
  disabled?: boolean;
  termsError?: string;
  privacyError?: string;
  /** Stable id for FormErrorSummary links (defaults to customer-auth-consent-terms). */
  termsInputId?: string;
  /** Stable id for FormErrorSummary links (defaults to customer-auth-consent-privacy). */
  privacyInputId?: string;
  /**
   * When false, hide optional marketing/analytics (sign-in legal recovery only).
   * Registration keeps the default `true`; optional re-prompts use MarketingAnalyticsConsentModal.
   */
  includeOptional?: boolean;
}

export function RegistrationConsentsField({
  value,
  onChange,
  disabled = false,
  termsError,
  privacyError,
  termsInputId = 'customer-auth-consent-terms',
  privacyInputId = 'customer-auth-consent-privacy',
  includeOptional = true,
}: RegistrationConsentsFieldProps): JSX.Element {
  const { t, i18n } = useTranslation('customer');
  const { apiBaseUrl } = useRuntimeConfig();
  const termsInvalid = termsError !== undefined && termsError.length > 0;
  const privacyInvalid = privacyError !== undefined && privacyError.length > 0;
  const locale =
    typeof i18n?.language === 'string' && i18n.language.length > 0
      ? i18n.language
      : undefined;
  const termsUrl = termsOfServiceDocumentUrl(apiBaseUrl, { locale });
  const privacyUrl = privacyNoticeDocumentUrl(apiBaseUrl, { locale });

  return (
    <fieldset className="flex flex-col gap-2 text-sm" disabled={disabled}>
      <legend className="mb-1 font-medium text-[var(--color-on-surface)]">
        {t('auth.consents.legend')}
      </legend>
      <label className="flex items-start gap-2" htmlFor={termsInputId}>
        <input
          id={termsInputId}
          type="checkbox"
          className="mt-1 h-4 w-4 accent-[var(--color-accent)]"
          checked={value.termsAccepted}
          onChange={(e) => {
            onChange({ ...value, termsAccepted: e.target.checked });
          }}
          aria-invalid={termsInvalid ? true : undefined}
        />
        <span className="text-[var(--color-on-surface-muted)]">
          {t('auth.consents.termsAcceptPrefix')}{' '}
          <a
            href={termsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-[var(--color-on-surface)]"
            data-testid="register-legal-terms-link"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {t('auth.consents.termsLink')}
          </a>
        </span>
      </label>
      <label className="flex items-start gap-2" htmlFor={privacyInputId}>
        <input
          id={privacyInputId}
          type="checkbox"
          className="mt-1 h-4 w-4 accent-[var(--color-accent)]"
          checked={value.privacyAccepted}
          onChange={(e) => {
            onChange({ ...value, privacyAccepted: e.target.checked });
          }}
          aria-invalid={privacyInvalid ? true : undefined}
        />
        <span className="text-[var(--color-on-surface-muted)]">
          {t('auth.consents.privacyAcceptPrefix')}{' '}
          <a
            href={privacyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-[var(--color-on-surface)]"
            data-testid="register-legal-privacy-link"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {t('auth.consents.privacyLink')}
          </a>
        </span>
      </label>
      {includeOptional ? (
        <>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-[var(--color-accent)]"
              checked={value.marketing}
              onChange={(e) => {
                onChange({ ...value, marketing: e.target.checked });
              }}
            />
            <span className="text-[var(--color-on-surface-muted)]">
              {t('auth.consents.marketingOptional')}
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-[var(--color-accent)]"
              checked={value.analytics}
              onChange={(e) => {
                onChange({ ...value, analytics: e.target.checked });
              }}
            />
            <span className="text-[var(--color-on-surface-muted)]">
              {t('auth.consents.analyticsOptional')}
            </span>
          </label>
        </>
      ) : null}
    </fieldset>
  );
}
